/**
 * Instantiates instant-upload widgets on all file inputs that carry
 * `data-instant-upload`.
 *
 * Features
 * --------
 *  • Uploads the file immediately when the user picks it (no wait for form submit)
 *  • Inline progress bar during upload
 *  • Thumbnail preview for images
 *  • Human-readable file-size badge
 *  • Drag & drop zone
 *  • Remove button to clear the selection
 *  • Stores the server-side ID in a hidden input so the form submission still works
 */

function formatBytes(bytes, decimals = 1) {
	if (bytes === 0) return "0 Bytes";
	const k = 1024;
	const dm = decimals < 0 ? 0 : decimals;
	const sizes = ["Bytes", "KB", "MB", "GB"];
	const i = Math.floor(Math.log(bytes) / Math.log(k));
	return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
}

function getCsrfToken() {
	// Try meta tag first (Django convention)
	const meta = document.querySelector('meta[name="csrf-token"]');
	if (meta) return meta.getAttribute("content");

	// Fall back to reading from cookie
	const name = "csrftoken";
	const ca = document.cookie.split(";");
	for (let i = 0; i < ca.length; i++) {
		let c = ca[i].trim();
		if (c.indexOf(name + "=") === 0) return decodeURIComponent(c.substring(name.length + 1));
	}
	return "";
}

class InstantFileUpload {
	constructor(container, uploadUrl) {
		this.container = container;
		this.uploadUrl = uploadUrl;
		this.fieldName = container.dataset.fieldName;
		this.fieldType = container.dataset.fieldType || "document"; // "image" or "document"
		this.required = container.dataset.required === "true";

		this.file = null;
		this.uploadedId = null;
	}

	/* ── Build the DOM ─────────────────────────────────────────── */
	init() {
		// Preserve label and help-text so the template-rendered markup survives
		const label = this.container.querySelector(".form-input--label");
		const helpText = this.container.querySelector(".form-input--help-text");
		const errorMessages = this.container.querySelector(".form-input--error-messages");

		// Hide the original file input (keep it in DOM as a click target)
		const origInput = this.container.querySelector('input[type="file"]');
		if (origInput) {
			origInput.style.display = "none";
			// Remove required so browser validation doesn't try to focus a hidden input
			origInput.removeAttribute("required");
		}

		// Build the widget HTML
		const widgetHTML = `
			<div class="file-upload-dropzone" tabindex="0" role="button" aria-label="Drop file here or click to browse">
				<div class="file-upload-dropzone__icon">
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
						<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
						<polyline points="17 8 12 3 7 8"/>
						<line x1="12" y1="3" x2="12" y2="15"/>
					</svg>
				</div>
				<p class="file-upload-dropzone__text">Drop file here or <strong>click to browse</strong></p>
			</div>

			<div class="file-upload-preview" style="display:none">
				<div class="file-upload-preview__row">
					<div class="file-upload-preview__info">
						<img class="file-upload-preview__thumb" src="" alt="preview" style="display:none"/>
						<div class="file-upload-preview__details">
							<span class="file-upload-preview__name"></span>
							<span class="file-upload-preview__size"></span>
						</div>
					</div>
					<button type="button" class="file-upload-preview__remove" aria-label="Remove file">
						<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
							<line x1="18" y1="6" x2="6" y2="18"/>
							<line x1="6" y1="6" x2="18" y2="18"/>
						</svg>
					</button>
				</div>
				<div class="file-upload-preview__progress" style="display:none">
					<div class="file-upload-preview__progress-bar"></div>
				</div>
			</div>

			<input type="hidden" name="${this.fieldName}_pre_uploaded_id" value=""/>
		`;

		// Replace only the file input with the widget, keep label/help-text/errors
		if (origInput) {
			origInput.insertAdjacentHTML("beforebegin", widgetHTML);
		} else {
			this.container.insertAdjacentHTML("beforeend", widgetHTML);
		}

		this.dropzone = this.container.querySelector(".file-upload-dropzone");
		this.preview = this.container.querySelector(".file-upload-preview");
		this.thumb = this.container.querySelector(".file-upload-preview__thumb");
		this.nameEl = this.container.querySelector(".file-upload-preview__name");
		this.sizeEl = this.container.querySelector(".file-upload-preview__size");
		this.progressWrap = this.container.querySelector(".file-upload-preview__progress");
		this.progressBar = this.container.querySelector(".file-upload-preview__progress-bar");
		this.removeBtn = this.container.querySelector(".file-upload-preview__remove");
		this.hiddenInput = this.container.querySelector('input[type="hidden"]');

		this._bindEvents();
	}

	/* ── Event binding ─────────────────────────────────────────── */
	_bindEvents() {
		this.dropzone.addEventListener("click", () => {
			const origInput = this.container.querySelector('input[type="file"]');
			if (origInput) origInput.click();
		});

		// Original input change
		const origInput = this.container.querySelector('input[type="file"]');
		if (origInput) {
			origInput.addEventListener("change", (e) => {
				if (e.target.files.length > 0) {
					this.handleFile(e.target.files[0]);
				}
			});
		}

		// Drag & drop
		this.dropzone.addEventListener("dragover", (e) => {
			e.preventDefault();
			e.stopPropagation();
			this.dropzone.classList.add("is-dragover");
		});

		this.dropzone.addEventListener("dragleave", (e) => {
			e.preventDefault();
			e.stopPropagation();
			this.dropzone.classList.remove("is-dragover");
		});

		this.dropzone.addEventListener("drop", (e) => {
			e.preventDefault();
			e.stopPropagation();
			this.dropzone.classList.remove("is-dragover");

			const files = e.dataTransfer.files;
			if (files.length > 0) {
				this.handleFile(files[0]);
			}
		});

		// Remove button
		this.removeBtn.addEventListener("click", () => {
			this.reset();
		});
	}

	/* ── Core logic ────────────────────────────────────────────── */
	async handleFile(file) {
		this.file = file;

		// Show preview area
		this.dropzone.style.display = "none";
		this.preview.style.display = "flex";

		// Set name & size
		this.nameEl.textContent = file.name;
		this.sizeEl.textContent = formatBytes(file.size);

		// Thumbnail for images
		if (file.type.startsWith("image/")) {
			const reader = new FileReader();
			reader.onload = (e) => {
				this.thumb.src = e.target.result;
				this.thumb.style.display = "block";
			};
			reader.readAsDataURL(file);
		} else {
			this.thumb.style.display = "none";
		}

		// Upload
		await this.upload(file);
	}

	async upload(file) {
		this.progressWrap.style.display = "block";
		this.progressBar.style.width = "0%";

		const formData = new FormData();
		formData.append("file", file);
		formData.append("field_type", this.fieldType);

		try {
			const xhr = new XMLHttpRequest();

			xhr.open("POST", this.uploadUrl, true);
			xhr.setRequestHeader("X-CSRFToken", getCsrfToken());

			xhr.upload.onprogress = (e) => {
				if (e.lengthComputable) {
					const percent = Math.round((e.loaded / e.total) * 100);
					this.progressBar.style.width = percent + "%";
				}
			};

			xhr.onload = () => {
				if (xhr.status === 200) {
					const response = JSON.parse(xhr.responseText);
					this.uploadedId = response.id;
					this.hiddenInput.value = response.id;

					// For images, replace local thumbnail with server thumbnail
					if (response.type === "image" && response.thumbnail) {
						this.thumb.src = response.thumbnail;
					}

					this.progressWrap.style.display = "none";
					this.progressBar.style.width = "100%";
					this.preview.classList.add("is-uploaded");
				} else {
					const response = JSON.parse(xhr.responseText);
					this.showError(response.error || "Upload failed");
				}
			};

			xhr.onerror = () => {
				this.showError("Network error — upload failed");
			};

			xhr.send(formData);
		} catch (err) {
			this.showError("Upload failed: " + err.message);
		}
	}

	showError(message) {
		this.progressWrap.style.display = "none";
		this.preview.classList.add("is-error");
		this.nameEl.textContent = message;
	}

	reset() {
		this.file = null;
		this.uploadedId = null;
		this.hiddenInput.value = "";
		this.thumb.src = "";
		this.thumb.style.display = "none";
		this.nameEl.textContent = "";
		this.sizeEl.textContent = "";
		this.progressBar.style.width = "0%";
		this.progressWrap.style.display = "none";
		this.preview.style.display = "none";
		this.preview.classList.remove("is-uploaded", "is-error");
		this.dropzone.style.display = "flex";

		// Reset original input
		const origInput = this.container.querySelector('input[type="file"]');
		if (origInput) origInput.value = "";
	}
}

/* ── Factory / init ───────────────────────────────────────────── */
function initFileUploads() {
	const uploadUrl = document.querySelector('meta[name="upload-url"]')?.getAttribute("content");

	if (!uploadUrl) {
		console.warn("fileUpload: no upload-url meta tag found");
		return;
	}

	document.querySelectorAll("[data-instant-upload]").forEach((container) => {
		const widget = new InstantFileUpload(container, uploadUrl);
		widget.init();
	});
}

export default initFileUploads;
